/**
 * Google Analytics 4 (GA4) helper utilities.
 *
 * The gtag snippet lives in public/index.html for local dev, but the
 * production webroot index.html is outside the FTP deploy root and may
 * not contain it.  initGA4() bootstraps gtag dynamically so tracking
 * works regardless of whether the HTML already includes the snippet.
 */

const GA4_ID = 'G-MZZ7PS2HXG'
const IS_PRODUCTION = process.env.NODE_ENV === 'production'

/**
 * Bootstrap GA4: creates dataLayer, defines window.gtag, and loads the
 * gtag.js script.  Safe to call multiple times — subsequent calls are
 * no-ops.  Skipped entirely in development to keep analytics clean.
 */
export function initGA4() {
  /* eslint-disable no-restricted-globals */
  if (!IS_PRODUCTION) return // skip in dev to avoid muddying analytics
  if (window.gtag) return // already initialised (e.g. via HTML)

  window.dataLayer = window.dataLayer || []
  window.gtag = function gtag() {
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', GA4_ID, { send_page_view: false })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`
  document.head.appendChild(script)
  /* eslint-enable no-restricted-globals */
}

function gtag() {
  if (window.gtag) {
    // eslint-disable-line no-restricted-globals
    window.gtag(...arguments) // eslint-disable-line no-restricted-globals
  }
}

// ── Page view (fired on every route change) ─────────────────────────
export function trackPageView(path, title) {
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
  })
}

// ── Generic custom event ─────────────────────────────────────────────
export function trackEvent(eventName, params = {}) {
  gtag('event', eventName, params)
}

// ── Travel / E-commerce events ───────────────────────────────────────

/** User views a tour or service detail page */
export function trackViewItem(item) {
  gtag('event', 'view_item', {
    currency: 'INR',
    value: item.price,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        item_category: item.category || 'tour',
        price: item.price,
      },
    ],
  })
}

/** User starts the booking form */
export function trackBeginCheckout(item, totalPrice) {
  gtag('event', 'begin_checkout', {
    currency: 'INR',
    value: totalPrice,
    items: [
      {
        item_id: item.id,
        item_name: item.name,
        item_category: item.category || 'tour',
        price: item.price,
      },
    ],
  })
}

/** Booking confirmed */
export function trackPurchase(booking) {
  gtag('event', 'purchase', {
    transaction_id: String(booking.id),
    currency: 'INR',
    value: booking.total_price,
    items: [
      {
        item_id: booking.item_id,
        item_name: booking.item_name,
        item_category: booking.category || 'tour',
        price: booking.total_price,
      },
    ],
  })
}

/** Contact form submitted */
export function trackContactSubmit() {
  gtag('event', 'generate_lead', { event_category: 'contact' })
}

/** User signs up */
export function trackSignUp(method = 'email') {
  gtag('event', 'sign_up', { method })
}

/** User logs in */
export function trackLogin(method = 'email') {
  gtag('event', 'login', { method })
}

/** Search performed (tours, services, etc.) */
export function trackSearch(searchTerm) {
  gtag('event', 'search', { search_term: searchTerm })
}
